import { Link } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import type { CatalogCategory } from '@/features/publicsite/types/catalog/CatalogCategory';
import type { CatalogCountsProps } from '@/features/publicsite/types/catalog/CatalogCountsProps';
import { publicCatalogCards } from '@/i18n/dictionaries/publicCatalogCards';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { useCatalogCounts } from '@/features/publicsite/catalogCounts';

/**
 * The nine AKIS catalogue categories (FR-001), with live totals.
 *
 * Shared between /catalog and the home page, exactly as the Thymeleaf fragment it replaces was —
 * `_catalog-cards.html` existed for that reason and this keeps the arrangement, so the nine
 * categories cannot drift between the two pages that show them.
 *
 * The map card has no total: "how many things are on a map" is not a number a visitor wants, so
 * it names the action instead. That was true of the template too.
 */
const CARDS: CatalogCategory[] = [
  {
    to: '/cercetare',
    title: 'researchTitle',
    description: 'researchDesc',
    count: 'research',
    unit: 'unitProjects',
  },
  {
    to: '/consilieri',
    title: 'advisorsTitle',
    description: 'advisorsDesc',
    count: 'advisors',
    unit: 'unitAdvisors',
  },
  {
    to: '/servicii',
    title: 'servicesTitle',
    description: 'servicesDesc',
    count: 'services',
    unit: 'unitServices',
  },
  {
    to: '/instruiri',
    title: 'trainingTitle',
    description: 'trainingDesc',
    count: 'trainings',
    unit: 'unitPrograms',
  },
  {
    to: '/biblioteca',
    title: 'libraryTitle',
    description: 'libraryDesc',
    count: 'library',
    unit: 'unitResources',
  },
  {
    to: '/finantare',
    title: 'fundingTitle',
    description: 'fundingDesc',
    count: 'funding',
    unit: 'unitCalls',
  },
  {
    to: '/evenimente',
    title: 'eventsTitle',
    description: 'eventsDesc',
    count: 'events',
    unit: 'unitEvents',
  },
  {
    to: '/comunitate',
    title: 'associationsTitle',
    description: 'associationsDesc',
    count: 'associations',
    unit: 'unitOrganisations',
  },
  { to: '/harta', title: 'mapTitle', description: 'mapDesc' },
];

export function CatalogCards({ counts }: CatalogCountsProps) {
  const { t } = useTranslation(publicCatalogCards);

  return (
    <>
      {CARDS.map((c) => (
        <Link className="card" to={c.to} key={c.to}>
          <span className="badge badge--right">{t('badgePublic')}</span>
          <h3>{t(c.title)}</h3>
          <p>{t(c.description)}</p>
          <span className="card__foot">
            <span className="card__count">
              {/* Empty until the totals arrive, rather than zero: "0 advisors" is a claim, and a
                  wrong one while the request is still in flight. */}
              {c.count && c.unit
                ? counts
                  ? t(c.unit, { n: counts[c.count] })
                  : ''
                : t('mapAction')}
            </span>
            <span className="card__arrow">→</span>
          </span>
        </Link>
      ))}
    </>
  );
}

/**
 * /catalog — the nine categories under a section head.
 *
 * The template opened this page with an <h2> and no <h1>, like the advisors and events pages did.
 */
export function CatalogPage() {
  const { t } = useTranslation(publicCatalogCards);
  usePublicPageTitle(t('heading'));
  const counts = useCatalogCounts();

  return (
    <section className="container section">
      <div className="section-head">
        <div>
          <span className="eyebrow">{t('eyebrow')}</span>
          <h1>{t('heading')}</h1>
          <p>{t('lead')}</p>
        </div>
        <Link className="head-link" to="/cautare">
          {t('searchAll')} <Icon name="arrow-right" className="link-arrow" />
        </Link>
      </div>
      <div className="card-grid">
        <CatalogCards counts={counts} />
      </div>
    </section>
  );
}
